import { useEffect, useRef, useState } from 'react';

import LockIcon from '../icons/LockIcon';
import LockOpenIcon from '../icons/LockOpenIcon';
import MinusIcon from '../icons/MinusIcon';
import MoveIcon from '../icons/MoveIcon';
import PlusIcon from '../icons/PlusIcon';
import ResetIcon from '../icons/ResetIcon';
import RotateIcon from '../icons/RotateIcon';
import Overlay from './Overlay';

type CameraInputMode = 'rotate' | 'truck' | 'dolly';

interface CameraControlsOverlayProps {
  overlay?: Pick<
    typeof Overlay,
    'Panel' | 'PanelHeader' | 'Content' | 'Button'
  >;
  disabled?: boolean;
  follow: boolean;
  frameCharacter: () => void;
  inputMode: CameraInputMode;
  setFollow: (follow: boolean) => void;
  setInputMode: (inputMode: CameraInputMode) => void;
  zoomIn: () => void;
  zoomOut: () => void;
  zoomOnHold?: boolean;
}

export default function CameraControlsOverlay({
  overlay: CameraOverlay = Overlay,
  disabled = false,
  follow,
  frameCharacter,
  inputMode,
  setFollow,
  setInputMode,
  zoomIn,
  zoomOut,
  zoomOnHold = true,
}: CameraControlsOverlayProps) {
  return (
    <CameraOverlay.Panel
      placement="start"
      widthClassName="w-[190px]"
      collapsible
      defaultCollapsed
    >
      <CameraOverlay.PanelHeader icon={<CameraIcon />} title="Camera" />
      <CameraOverlay.Content>
        <Overlay.Group orientation="vertical">
          <CameraOverlay.Button
            tooltipText="Rotate"
            text="Rotate"
            active={inputMode === 'rotate'}
            icon={<RotateIcon className="size-4" />}
            onClick={() => setInputMode('rotate')}
            disabled={disabled}
          />
          <CameraOverlay.Button
            tooltipText="Pan"
            text="Pan"
            active={inputMode === 'truck'}
            icon={<MoveIcon className="size-4" />}
            onClick={() => setInputMode('truck')}
            disabled={disabled}
          />
          <CameraZoomButtons
            Button={CameraOverlay.Button}
            zoomOnHold={zoomOnHold}
            zoomIn={zoomIn}
            zoomOut={zoomOut}
            disabled={disabled}
          />
          <CameraOverlay.Button
            tooltipText="Frame character"
            text="Frame"
            icon={<ResetIcon className="size-4" />}
            onClick={frameCharacter}
            disabled={disabled}
          />
          <CameraOverlay.Button
            tooltipText="Follow"
            text="Lock"
            active={follow}
            icon={
              follow ? (
                <LockIcon className="size-4" />
              ) : (
                <LockOpenIcon className="size-4" />
              )
            }
            onClick={() => setFollow(!follow)}
            disabled={disabled}
          />
        </Overlay.Group>
      </CameraOverlay.Content>
    </CameraOverlay.Panel>
  );
}

interface CameraZoomButtonsProps {
  Button: typeof Overlay.Button;
  disabled?: boolean;
  zoomIn: () => void;
  zoomOut: () => void;
  zoomOnHold: boolean;
}

function CameraZoomButtons({
  Button,
  disabled = false,
  zoomIn,
  zoomOut,
  zoomOnHold,
}: CameraZoomButtonsProps) {
  const [mouseDown, setMouseDown] = useState<'in' | 'out' | ''>('');
  const interval = useRef<number | null>(null);

  useEffect(() => {
    if (interval.current) {
      window.clearInterval(interval.current);
    }

    if (mouseDown === 'in') {
      interval.current = window.setInterval(zoomIn, 150);
    } else if (mouseDown === 'out') {
      interval.current = window.setInterval(zoomOut, 150);
    }

    return () => {
      if (interval.current) {
        window.clearInterval(interval.current);
      }
    };
  }, [mouseDown, zoomIn, zoomOut]);

  return (
    <>
      <Button
        tooltipText="Zoom in"
        text="Zoom in"
        icon={<PlusIcon className="size-4" />}
        onClick={zoomOnHold ? undefined : zoomIn}
        onMouseDown={() => {
          if (!zoomOnHold) return;
          zoomIn();
          setMouseDown('in');
        }}
        onMouseUp={() => setMouseDown('')}
        onMouseLeave={() => setMouseDown('')}
        disabled={disabled}
      />
      <Button
        tooltipText="Zoom out"
        text="Zoom out"
        icon={<MinusIcon className="size-4" />}
        onClick={zoomOnHold ? undefined : zoomOut}
        onMouseDown={() => {
          if (!zoomOnHold) return;
          zoomOut();
          setMouseDown('out');
        }}
        onMouseUp={() => setMouseDown('')}
        onMouseLeave={() => setMouseDown('')}
        disabled={disabled}
      />
    </>
  );
}

const CameraIcon = ({ className }: { className?: string }) => {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <path d="M15.7692 5.19565H13.7702L12.7873 3.75152C12.7346 3.67419 12.6633 3.61078 12.5796 3.56691C12.496 3.52304 12.4026 3.50006 12.3077 3.5H7.69231C7.59742 3.50006 7.50402 3.52304 7.42037 3.56691C7.33672 3.61078 7.2654 3.67419 7.21274 3.75152L6.22909 5.19565H4.23077C3.77174 5.19565 3.33151 5.3743 3.00693 5.6923C2.68235 6.01029 2.5 6.44159 2.5 6.8913V14.8043C2.5 15.2541 2.68235 15.6854 3.00693 16.0034C3.33151 16.3214 3.77174 16.5 4.23077 16.5H15.7692C16.2283 16.5 16.6685 16.3214 16.9931 16.0034C17.3177 15.6854 17.5 15.2541 17.5 14.8043V6.8913C17.5 6.44159 17.3177 6.01029 16.9931 5.6923C16.6685 5.3743 16.2283 5.19565 15.7692 5.19565ZM16.3462 14.8043C16.3462 14.9543 16.2854 15.098 16.1772 15.204C16.069 15.31 15.9222 15.3696 15.7692 15.3696H4.23077C4.07776 15.3696 3.93102 15.31 3.82282 15.204C3.71463 15.098 3.65385 14.9543 3.65385 14.8043V6.8913C3.65385 6.7414 3.71463 6.59763 3.82282 6.49164C3.93102 6.38564 4.07776 6.32609 4.23077 6.32609H6.53846C6.63347 6.32615 6.72702 6.30322 6.81081 6.25934C6.89459 6.21546 6.96602 6.15199 7.01875 6.07457L8.00096 4.63043H11.9983L12.9812 6.07457C13.034 6.15199 13.1054 6.21546 13.1892 6.25934C13.273 6.30322 13.3665 6.32615 13.4615 6.32609H15.7692C15.9222 6.32609 16.069 6.38564 16.1772 6.49164C16.2854 6.59763 16.3462 6.7414 16.3462 6.8913V14.8043ZM10 7.45652C9.37242 7.45652 8.75894 7.63884 8.23713 7.98043C7.71532 8.32202 7.30862 8.80753 7.06846 9.37557C6.8283 9.94361 6.76546 10.5687 6.88789 11.1717C7.01033 11.7747 7.31253 12.3286 7.7563 12.7634C8.20006 13.1982 8.76545 13.4942 9.38096 13.6142C9.99648 13.7341 10.6345 13.6726 11.2143 13.4373C11.7941 13.202 12.2897 12.8035 12.6383 12.2923C12.987 11.7811 13.1731 11.1801 13.1731 10.5652C13.1721 9.74103 12.8375 8.95086 12.2427 8.36807C11.6478 7.78528 10.8413 7.45746 10 7.45652ZM10 12.5435C9.60063 12.5435 9.21024 12.4275 8.87818 12.2101C8.54611 11.9927 8.2873 11.6837 8.13447 11.3223C7.98164 10.9608 7.94166 10.563 8.01957 10.1793C8.09748 9.79553 8.28979 9.44304 8.57219 9.16638C8.85458 8.88971 9.21437 8.7013 9.60607 8.62497C9.99776 8.54864 10.4038 8.58781 10.7727 8.73754C11.1417 8.88727 11.4571 9.14083 11.6789 9.46615C11.9008 9.79148 12.0192 10.174 12.0192 10.5652C12.0192 11.0899 11.8065 11.5931 11.4278 11.9641C11.0491 12.3351 10.5355 12.5435 10 12.5435Z" />
    </svg>
  );
};
